import type * as Y from "yjs";
import {
  deleteRow,
  deleteSection,
  listPanels,
  listRows,
  listSections,
  newPanel,
  type Panel,
  type PanelMaps,
  type PanelRow,
  patchPanel,
  putPanel,
  putRow,
  putSection,
  runLocal,
  type Section,
} from "@electro-planner/domain";

/**
 * Fileformat för .panelspec-export/import:
 *
 *   { magic: "panelspec", version: 1, panel, sections, rows, exportedAt }
 *
 * Magic + version används som format-kontroll. Framtida ändringar höjer version;
 * importern stödjer då flera versioner och migrerar till aktuell.
 */
type ShareFile = {
  magic: "panelspec";
  version: 1;
  exportedAt: string;
  panel: Panel | undefined;
  sections: ReadonlyArray<Section>;
  rows: ReadonlyArray<PanelRow>;
};

const CURRENT_VERSION = 1;

export function sharePanelFile(input: {
  panel: Panel | undefined;
  sections: ReadonlyArray<Section>;
  rows: ReadonlyArray<PanelRow>;
}): void {
  const data: ShareFile = {
    magic: "panelspec",
    version: CURRENT_VERSION,
    exportedAt: new Date().toISOString(),
    panel: input.panel,
    sections: input.sections,
    rows: input.rows,
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${input.panel?.designation ?? "central"}.panelspec.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Plockar upp en .panelspec-fil från en File (t.ex. <input type="file">) och
 * returnerar den parsade payloaden. Kastar ett begripligt Error vid felaktig
 * fil så anroparen kan visa det för användaren.
 */
export async function readPanelSpecFile(file: File): Promise<ShareFile> {
  const text = await file.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("Filen är inte giltig JSON.");
  }
  if (!data || typeof data !== "object") {
    throw new Error("Filinnehållet är inte ett objekt.");
  }
  const rec = data as Record<string, unknown>;
  if (rec.magic !== "panelspec") {
    throw new Error(
      "Detta är inte en .panelspec-fil (saknar magic-fält).",
    );
  }
  if (typeof rec.version !== "number" || rec.version > CURRENT_VERSION) {
    throw new Error(
      `Okänd version (${String(rec.version)}). Uppdatera Centralspec och försök igen.`,
    );
  }
  return rec as unknown as ShareFile;
}

/**
 * Applicerar en importerad .panelspec-payload på en Y.Doc. Samma strategi som
 * applyTemplate: töm först, skriv sedan in panel + sektioner + rader. Behåller
 * de id:n som fanns i importen så interna referenser (sectionId på rader) pekar
 * rätt.
 */
export function applyPanelSpecImport(
  doc: Y.Doc,
  maps: PanelMaps,
  payload: ShareFile,
): void {
  runLocal(doc, () => {
    for (const r of listRows(maps)) deleteRow(maps, r.id);
    for (const s of listSections(maps)) deleteSection(maps, s.id);

    const panels = listPanels(maps);
    if (payload.panel) {
      if (panels.length > 0) {
        patchPanel(maps, panels[0]!.id, stripIdCreated(payload.panel));
      } else {
        putPanel(maps, newPanel(stripIdCreated(payload.panel)));
      }
    }

    for (const s of payload.sections) putSection(maps, s);
    for (const r of payload.rows) putRow(maps, r);
  });
}

function stripIdCreated(p: Panel): Partial<Panel> {
  const { id: _id, createdAt: _ca, ...rest } = p;
  void _id;
  void _ca;
  return rest;
}

// --- Pending-import via sessionStorage (speglar pending-template-flödet) ---

const PENDING_KEY = "electro-planner-panels:pending-import";

export function setPendingImport(documentId: string, payload: ShareFile): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(
    `${PENDING_KEY}:${documentId}`,
    JSON.stringify(payload),
  );
}

export function takePendingImport(documentId: string): ShareFile | null {
  if (typeof sessionStorage === "undefined") return null;
  const key = `${PENDING_KEY}:${documentId}`;
  const val = sessionStorage.getItem(key);
  if (!val) return null;
  sessionStorage.removeItem(key);
  try {
    return JSON.parse(val) as ShareFile;
  } catch {
    return null;
  }
}
